import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { familyApi, sharingApi, workspacesApi } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/shared/Toast";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";
import BackButtonTrap from "../components/shared/BackButtonTrap";
import useBackButton from "../hooks/useBackButton";

const GROUPS = ["People & Gifts", "Food", "Events & Trips", "Pets"];
const MODULE_META = {
  "People & Gifts": { icon: "🎁", color: "#F3F0FF", url: "/app/people" },
  Food:             { icon: "🍽️", color: "#F0FDF4", url: "/app/food" },
  "Events & Trips": { icon: "✈️", color: "#FFF7ED", url: "/app/events" },
  Pets:             { icon: "🐾", color: "#ECFDF5", url: "/app/pets" },
};
const AVATAR_COLORS = ["#2A7B6F", "#8B5CF6", "#F59E0B", "#3B82F6", "#EF4444"];

const INVITE_COLORS = [
  "#7C5CBF", "#2A7B6F", "#C47B2B", "#C4392B", "#1D5FA8", "#1A7A3C", "#8A3A6B", "#4A4640",
];

const INVITE_ROLES = [
  { id: "member",  icon: "\u{1F46A}", title: "Family member",  desc: "Gets their own private account, shares the Family AI pool, and sees only items explicitly shared with them" },
];

function memberProfilePicture(member) {
  return member?.profile_picture || member?.profile_picture_url || member?.avatar_url || "";
}

/* \u2500\u2500 3-step Invite Sheet \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
function InviteSheet({ isOpen, onClose, onSuccess, onWorkingChange, currentUser }) {
  useBackButton(onClose, isOpen);
  const [step, setStep]             = useState(1);
  const [role, setRole]             = useState(INVITE_ROLES[0]);
  const [name, setName]             = useState("");
  const [color, setColor]           = useState(INVITE_COLORS[0]);
  const [relationship, setRelationship] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [sending, setSending]       = useState(false);
  const [err, setErr]               = useState("");
  const sheetRef = useRef(null);
  const emailRef = useRef(null);
  const nameRef  = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setStep(1); setRole(INVITE_ROLES[0]); setName(""); setColor(INVITE_COLORS[0]);
      setRelationship(""); setEmailInput(""); setInviteLink(""); setErr("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 2) setTimeout(() => nameRef.current?.focus(), 80);
  }, [step]);

  useEffect(() => {
    const el = sheetRef.current;
    if (!el || !isOpen) return;
    let startY = 0, delta = 0;
    const onStart = e => { startY = e.touches[0].clientY; delta = 0; el.style.transition = "none"; };
    const onMove  = e => { delta = Math.max(0, e.touches[0].clientY - startY); el.style.transform = `translateY(${delta}px)`; };
    const onEnd   = () => {
      if (delta > 80) {
        el.style.transition = "transform .22s cubic-bezier(.4,0,1,1)";
        el.style.transform  = "translateY(110%)";
        setTimeout(onClose, 230);
      } else {
        el.style.transition = "transform .2s cubic-bezier(.2,0,0,1)";
        el.style.transform  = "translateY(0)";
        setTimeout(() => { if (el) el.style.transition = ""; }, 210);
      }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove",  onMove,  { passive: true });
    el.addEventListener("touchend",   onEnd,   { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove",  onMove);
      el.removeEventListener("touchend",   onEnd);
      el.style.transform = "";
      el.style.transition = "";
      el.style.animation = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const initial = name.trim() ? name.trim()[0].toUpperCase() : "?";
  const ownerInitial = (currentUser?.full_name || currentUser?.email || "?")[0].toUpperCase();
  const ownerPhoto = memberProfilePicture(currentUser);

  async function handleEmailSend() {
    const email = emailInput.trim();
    if (!email) { setErr("Enter their email address."); emailRef.current?.focus(); return; }
    setSending(true); setErr("");
    onWorkingChange?.("Sending invite…");
    try {
      const res = await familyApi.createInvite({
        email,
        role: role.id,
        name: name.trim(),
        avatar_color: color,
        relationship,
      });
      if (res?.invite_url && !res?.invite_email_sent && !res?.added_now) {
        setInviteLink(res.invite_url);
        setErr(res.message || "Email could not be sent. Copy the invite link instead.");
        return;
      }
      onSuccess(res?.message || (res?.added_now ? `${email} added to your Family plan` : `Invite sent to ${email}`));
      if (res?.added_now || res?.accepted) {
        window.dispatchEvent(new Event("hm_notifications_refresh"));
      }
      onClose();
    } catch (e) {
      setErr(e.message || "Could not send invite. Try again.");
    } finally {
      setSending(false);
      onWorkingChange?.("");
    }
  }

  function handleCopyInviteLink() {
    if (!inviteLink) return;
    navigator.clipboard?.writeText(inviteLink).catch(() => {});
    onSuccess("Invite link copied. They will be added automatically when they accept.");
    onClose();
  }

  function handlePrimaryInvite() {
    if (step < 3) {
      if (step === 2 && !name.trim()) { nameRef.current?.focus(); return; }
      setStep(s => s + 1);
      return;
    }
    handleEmailSend();
  }

  const STEP_LABELS = { 1: "STEP 1 OF 3 \u2014 Their role", 2: "STEP 2 OF 3 \u2014 Their name", 3: "STEP 3 OF 3 \u2014 Send invite" };

  return (
    <div className="hm-sheet-overlay" onClick={onClose}>
      <div className="hm-invite-sheet" ref={sheetRef} onClick={e => e.stopPropagation()}>
        <div className="hm-sheet-handle" />
        <div className="hm-inv-hero">
          <div className="hm-inv-hero-label">{"\u{1F465} Family sharing"}</div>
          <div className="hm-inv-hero-title">Invite someone <em>you trust</em></div>
          <div className="hm-inv-hero-sub">{"They'll get their own private account. Your data stays private unless you share a specific item."}</div>
        </div>
        <div className="hm-inv-steps">
          {[1, 2, 3].map(n => (
            <div key={n} className={`hm-inv-dot ${n < step ? "is-done" : n === step ? "is-active" : ""}`} />
          ))}
          <span className="hm-inv-step-lbl">{STEP_LABELS[step]}</span>
        </div>

        {step === 1 && (
          <div className="hm-inv-body">
            <p className="hm-inv-hint">Family accounts share one AI pool. Household data stays separate by default.</p>
            <div className="hm-inv-role-grid">
              {INVITE_ROLES.map(r => (
                <button key={r.id} type="button" className={`hm-inv-role-card${role.id === r.id ? " is-on" : ""}`} onClick={() => setRole(r)}>
                  <span className="hm-inv-rc-icon">{r.icon}</span>
                  <span className="hm-inv-rc-body">
                    <span className="hm-inv-rc-title">{r.title}</span>
                    <span className="hm-inv-rc-desc">{r.desc}</span>
                  </span>
                  <span className="hm-inv-rc-check">{role.id === r.id ? "\u2713" : ""}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="hm-inv-body">
            <div className="hm-inv-preview">
              <div className="hm-inv-prev-av" style={{ background: color }}>{initial}</div>
              <div>
                <div className="hm-inv-prev-name">{name.trim() || "Their name"}</div>
                <div className="hm-inv-prev-role">{role.title}</div>
              </div>
            </div>
            <label className="hm-inv-label">Their name *</label>
            <input ref={nameRef} className="hm-inv-input" placeholder={"e.g. Sally, Mom, Partner\u2026"} value={name} onChange={e => setName(e.target.value)} />
            <label className="hm-inv-label">Avatar colour</label>
            <div className="hm-inv-color-row">
              {INVITE_COLORS.map(c => (
                <button key={c} type="button" className={`hm-inv-color-dot${color === c ? " is-on" : ""}`} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Color ${c}`}>{initial}</button>
              ))}
            </div>
            <label className="hm-inv-label">Relationship <span className="hm-inv-opt">(optional)</span></label>
            <select className="hm-inv-input" value={relationship} onChange={e => setRelationship(e.target.value)}>
              <option value="">Choose relationship</option>
              {["Partner / Spouse","Parent","Child","Sibling","Roommate","Friend","Other"].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )}

        {step === 3 && (
          <div className="hm-inv-body">
            <div className="hm-inv-preview-card">
              <div className="hm-inv-pc-label">{"\u2709 Invite preview"}</div>
              <div className="hm-inv-pc-msg">"{name.trim() ? `Hey ${name.trim()}! ` : "Hey! "}I'm using HouseMind to manage our household. Join me and let's stay organised together."</div>
              <div className="hm-inv-pc-from">
                {ownerPhoto
                  ? <img className="hm-inv-pc-av hm-inv-pc-av--photo" src={ownerPhoto} alt={currentUser?.full_name || "You"} />
                  : <span className="hm-inv-pc-av">{ownerInitial}</span>
                }
                From {currentUser?.full_name?.split(" ")[0] || "you"} {"\u00b7 HouseMind household"}
              </div>
            </div>
            <div className="hm-inv-methods-label">Add by email</div>
            <p className="hm-inv-method-note">
              If they already have a HouseMind account, they will be added now. If not, we will email an invite link and add them automatically when they accept.
            </p>
            <div className="hm-inv-email-form">
              <label className="hm-inv-label">Their email address</label>
              <input ref={emailRef} className="hm-inv-input" type="email" placeholder="friend@example.com" value={emailInput} onChange={e => { setEmailInput(e.target.value); setInviteLink(""); setErr(""); }} onKeyDown={e => e.key === "Enter" && handleEmailSend()} />
              {err && <div className="hm-inv-error">{err}</div>}
              {inviteLink && (
                <button type="button" className="hm-inv-send-btn" onClick={handleCopyInviteLink}>
                  Copy invite link
                </button>
              )}
            </div>
          </div>
        )}

        {(
          <div className="hm-inv-footer">
            {step > 1 && <button type="button" className="hm-inv-btn-back" onClick={() => setStep(s => s - 1)}>{"\u2190 Back"}</button>}
            <button type="button" className="hm-inv-btn-next" onClick={handlePrimaryInvite} disabled={sending}>
              {step < 3 ? "Next \u2192" : sending ? "Sending\u2026" : "Add member \u2713"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const RESOURCE_ICON = {
  recipe:        "🍳",
  meal_plan:     "📅",
  shopping_list: "🛒",
  pantry:        "🧺",
  pantry_item:   "🧺",
  person:        "👤",
  event:         "📅",
  trip:          "✈️",
  pet:           "🐾",
};
const DEFAULT_REACTIONS = ["🔥", "😍", "👌", "🤤"];

function initials(name = "", email = "") {
  const src = name || email || "?";
  return src.split(/[ @._-]+/).filter(Boolean).map(p => p[0]).join("").slice(0, 2).toUpperCase();
}

function firstName(name = "", email = "") {
  const clean = String(name || "").trim();
  if (clean) return clean.split(/\s+/)[0];
  return (String(email || "").split("@")[0] || "Member").split(/[._-]+/)[0];
}

function fmtDate(value) {
  if (!value) return "next month";
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function timeAgo(value) {
  if (!value) return "";
  const then = new Date(value);
  const diff = Date.now() - then.getTime();
  if (Number.isNaN(diff)) return "";
  const minutes = Math.max(1, Math.round(diff / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function expiryText(item) {
  if (item?.archived_at) return { text: "Expired · Archived", tone: "is-muted" };
  if (!item?.expires_at) return null;
  const expires = new Date(item.expires_at);
  if (Number.isNaN(expires.getTime())) return null;
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startExpiry = new Date(expires.getFullYear(), expires.getMonth(), expires.getDate());
  const days = Math.ceil((startExpiry - startToday) / 86400000);
  if (days < 0) return { text: "Expired · Archived", tone: "is-muted" };
  if (days === 0) return { text: "Expires today", tone: "is-danger" };
  if (days === 1) return { text: "Expires tomorrow", tone: "is-danger" };
  if (days <= 3) return { text: `Expires in ${days} days`, tone: "is-warn" };
  return { text: `Expires in ${days} days`, tone: "" };
}

function countdownTone(days) {
  if (days <= 7) return "is-urgent";
  if (days <= 14) return "is-soon";
  return "";
}

function grouped(summary, key) {
  return GROUPS.map(label => ({
    label,
    items: summary?.[key]?.[label] || [],
    meta: MODULE_META[label],
  })).filter(g => g.items.length > 0);
}

/* ── Compact share row ─────────────────────────────────────────────────── */
function MemberPip({ name, color, avatarUrl }) {
  if (avatarUrl) {
    return <img className="hm-fsr-pip hm-fsr-pip--photo" src={avatarUrl} alt={name} />;
  }
  return (
    <span className="hm-fsr-pip" style={{ background: color }}>
      {name ? name[0].toUpperCase() : "?"}
    </span>
  );
}

function ShareRow({ item, direction, memberColors, memberAvatars, onAction }) {
  const name = direction === "with"
    ? firstName(item.owner_name, item.owner_email)
    : firstName(item.recipient_name, item.recipient_email);

  const dotColor  = memberColors[name]  || "#8B5CF6";
  const avatarUrl = memberAvatars[name] || null;
  const icon = RESOURCE_ICON[item.resource_type] || "•";
  const meta = [
    item.resource_type === "recipe" ? "Recipe" :
    item.resource_type === "meal_plan" ? "Meal plan" :
    item.resource_type === "shopping_list" ? "Shopping" :
    item.resource_type === "pantry" ? "Pantry" :
    item.resource_type === "person" ? "Person" :
    item.resource_type === "trip" ? "Trip" :
    item.resource_type === "event" ? "Event" :
    item.resource_type === "pet" ? "Pet" : "Item",
    item.summary,
  ].filter(Boolean).join(" · ");
  const expiry = expiryText(item);

  return (
    <div className="hm-fsr" onClick={onAction} role="button" tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onAction()}>
      <span className="hm-fsr-icon">{icon}</span>
      <span className="hm-fsr-body">
        <span className="hm-fsr-title">{item.title}</span>
        <span className="hm-fsr-meta">{meta}</span>
        {expiry && <span className={`hm-fsr-expiry ${expiry.tone}`}>{expiry.text}</span>}
      </span>
      <span className="hm-fsr-right">
        <span className="hm-fsr-who">
          <MemberPip name={name} color={dotColor} avatarUrl={avatarUrl} />
          {name}
        </span>
        <span className="hm-fsr-chevron">›</span>
      </span>
    </div>
  );
}

/* ── Action bottom sheet ───────────────────────────────────────────────── */
function ActionSheet({ item, direction, memberColors, memberAvatars, onClose, onRemove, onOpenModule, onChangePermission, groupMeta, sheetRef }) {
  const [permissionPickerOpen, setPermissionPickerOpen] = useState(false);
  if (!item) return null;
  const name = direction === "with"
    ? firstName(item.owner_name, item.owner_email)
    : firstName(item.recipient_name, item.recipient_email);
  const dotColor  = memberColors[name]  || "#8B5CF6";
  const avatarUrl = memberAvatars[name] || null;
  const canEdit = (item.permission || "view") === "edit";
  const permissionLabel = canEdit ? (item.resource_type === "pet" ? "Help with care" : "Can edit") : "View only";
  const canChangePermission = ["shopping_list", "pet"].includes(item.resource_type);
  const archived = Boolean(item.archived_at);

  return (
    <div className="hm-sheet-overlay" onClick={onClose}>
      <div
        className="hm-action-sheet hm-family-standard-sheet"
        ref={sheetRef}
        onClick={e => e.stopPropagation()}
      >
        <div className="hm-sheet-handle" />
        <div className="hm-inv-hero hm-family-sheet-hero">
          <div className="hm-inv-hero-label">Family sharing</div>
          <div className="hm-inv-hero-title">{item.title}</div>
          <div className="hm-inv-hero-sub hm-family-sheet-sub">
            <MemberPip name={name} color={dotColor} avatarUrl={avatarUrl} />
            <span>{direction === "with" ? `From ${name}` : `Shared with ${name} · ${permissionLabel}`}</span>
          </div>
        </div>
        <div className="hm-sheet-actions">
          {archived ? (
            <div className="hm-sa" role="status">
              <span className="hm-sa-ico" style={{ background: "var(--hm-surface-strong)" }}>⌛</span>
              <span className="hm-sa-body">
                <span className="hm-sa-title">Expired and archived</span>
                <span className="hm-sa-sub">Time-sensitive shared content is kept briefly, then removed automatically.</span>
              </span>
            </div>
          ) : (
            <button className="hm-sa" onClick={() => { onClose(); onOpenModule(); }}>
              <span className="hm-sa-ico" style={{ background: "var(--hm-surface-strong)" }}>↗</span>
              <span className="hm-sa-body">
                <span className="hm-sa-title">Open item</span>
                <span className="hm-sa-sub">View the full content</span>
              </span>
              <span className="hm-sa-arr">›</span>
            </button>
          )}
          {direction === "by" && canChangePermission && !archived && (
            <>
              <button className="hm-sa" onClick={() => setPermissionPickerOpen(open => !open)}>
                <span className="hm-sa-ico" style={{ background: "color-mix(in srgb, var(--hm-amber) 14%, transparent)" }}>
                  {canEdit ? "✏️" : "🔒"}
                </span>
                <span className="hm-sa-body">
                  <span className="hm-sa-title">Change permission</span>
                  <span className="hm-sa-sub">Current access: {permissionLabel}</span>
                </span>
                <span className="hm-sa-arr">{permissionPickerOpen ? "⌃" : "›"}</span>
              </button>
              {permissionPickerOpen && (
                <div style={{ display: "grid", gap: 8, padding: "0 12px 10px 60px" }}>
                  <button
                    type="button"
                    className="hm-sa"
                    style={{
                      minHeight: 54,
                      border: canEdit ? "1px solid var(--hm-border)" : "1px solid var(--hm-mint)",
                      borderRadius: 14,
                      background: canEdit ? "var(--hm-surface)" : "color-mix(in srgb, var(--hm-mint) 14%, white)",
                    }}
                    onClick={() => onChangePermission("view")}
                  >
                    <span className="hm-sa-ico">🔒</span>
                    <span className="hm-sa-body">
                      <span className="hm-sa-title">View only</span>
                      <span className="hm-sa-sub">{name} can open this item but cannot change it.</span>
                    </span>
                    <span className="hm-sa-arr">{!canEdit ? "✓" : ""}</span>
                  </button>
                  <button
                    type="button"
                    className="hm-sa"
                    style={{
                      minHeight: 54,
                      border: canEdit ? "1px solid var(--hm-mint)" : "1px solid var(--hm-border)",
                      borderRadius: 14,
                      background: canEdit ? "color-mix(in srgb, var(--hm-mint) 14%, white)" : "var(--hm-surface)",
                    }}
                    onClick={() => onChangePermission("edit")}
                  >
                    <span className="hm-sa-ico">✏️</span>
                    <span className="hm-sa-body">
                      <span className="hm-sa-title">{item.resource_type === "pet" ? "Help with care" : "Can edit"}</span>
                      <span className="hm-sa-sub">{item.resource_type === "pet" ? `${name} can mark today's pet routines complete.` : `${name} can update the shared item.`}</span>
                    </span>
                    <span className="hm-sa-arr">{canEdit ? "✓" : ""}</span>
                  </button>
                </div>
              )}
            </>
          )}
          {!archived && (
            <>
              <div className="hm-sa-divider" />
              <button className="hm-sa hm-sa--danger" onClick={() => { onClose(); onRemove(); }}>
                <span className="hm-sa-ico" style={{ background: "color-mix(in srgb, var(--hm-red) 12%, transparent)" }}>✕</span>
                <span className="hm-sa-body">
                  <span className="hm-sa-title">{direction === "with" ? "Remove from my view" : "Stop sharing"}</span>
                  <span className="hm-sa-sub">
                    {direction === "with" ? "Hide this item from Shared with me" : `Remove ${name}'s access to this item`}
                  </span>
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Empty state ───────────────────────────────────────────────────────── */
function EmptyState({ mode }) {
  return (
    <div className="hm-family-empty">
      <div>{mode === "with" ? "📭" : "🔒"}</div>
      <strong>{mode === "with" ? "Nothing shared with you yet" : "You haven't shared anything yet"}</strong>
      <span>{mode === "with" ? "When a Family member shares an item, it appears here." : "Use Share on supported Food, People & Gifts, Events, and Pets items."}</span>
    </div>
  );
}

function HouseholdStats({ stats }) {
  const cards = [
    ["🍽️ Meals planned", stats?.meals_planned || 0],
    ["📝 List items", stats?.list_items || 0],
    ["🎉 Occasions soon", stats?.occasions_soon || 0],
    ["✈️ Trips this month", stats?.trips_this_month || 0],
  ];
  return (
    <section className="hm-family-stats-grid">
      {cards.map(([label, value]) => (
        <div key={label} className="hm-family-stat-card">
          <strong>{value}</strong>
          <span>{label}</span>
        </div>
      ))}
    </section>
  );
}

function ComingUpBar({ items, onOpen }) {
  if (!items?.length) return null;
  return (
    <section className="hm-family-coming">
      <div className="hm-family-coming-head">
        <strong>🎉 Coming up</strong>
        <span>Shared occasions</span>
      </div>
      {items.slice(0, 3).map((item, index) => (
        <button key={`${item.share_id}-${index}`} type="button" className="hm-family-coming-row" onClick={() => item.share_id && onOpen(item.share_id)}>
          <span className="hm-family-coming-icon">{item.icon || "🎉"}</span>
          <span className="hm-family-coming-body">
            <strong>{item.occasion_name}{item.person_name ? ` · ${item.person_name}` : ""}</strong>
            <small>{item.tracking}</small>
          </span>
          <span className={`hm-family-countdown ${countdownTone(item.days_until)}`}>
            {item.days_until === 0 ? "today" : `${item.days_until}d`}
          </span>
        </button>
      ))}
    </section>
  );
}

function cookingTimeLabel(mealType) {
  if (mealType === "breakfast") return "this morning";
  if (mealType === "lunch") return "this afternoon";
  return "tonight";
}

function cookingMealEmoji(mealType) {
  if (mealType === "breakfast") return "🍳";
  if (mealType === "lunch") return "🥗";
  return "🍽️";
}

function CookingTonightBar({ items, currentUserId, onClear }) {
  if (!items?.length) return null;
  return (
    <section className="hm-family-cooking-now">
      {items.map((item, index) => {
        const isMe = item.user_id && currentUserId && item.user_id === currentUserId;
        const timeLabel = cookingTimeLabel(item.meal_type);
        return (
          <div key={`${item.member_email || item.member_name}-${index}`} className="hm-family-cooking-row">
            <span className="hm-family-live-dot" />
            <span className="hm-family-cooking-emoji">{cookingMealEmoji(item.meal_type)}</span>
            <strong>{isMe ? "You are" : `${firstName(item.member_name, item.member_email)} is`} cooking <em>{item.meal_name}</em> {timeLabel}</strong>
            {isMe && onClear && (
              <button type="button" className="hm-family-cooking-clear" onClick={onClear} aria-label="Clear my cooking status">✕</button>
            )}
          </div>
        );
      })}
    </section>
  );
}

function FeedItem({ item, onReact, onOpenShare }) {
  const reacted = new Map((item.reactions || []).map(reaction => [reaction.emoji, reaction]));
  return (
    <article className="hm-family-feed-item">
      <span className="hm-family-feed-avatar">{initials(item.member_name, item.member_email).slice(0, 1)}</span>
      <div className="hm-family-feed-body">
        <strong>{item.action_text}</strong>
        <small>◷ {timeAgo(item.created_at)}</small>
        {item.content_preview?.name && (
          <button type="button" className="hm-family-feed-preview" onClick={() => item.share_id && onOpenShare(item.share_id)}>
            <span>{item.content_preview.icon || "•"}</span>
            <em>{item.content_preview.name}</em>
            {item.content_preview.action_label && <b>{item.content_preview.action_label}</b>}
          </button>
        )}
        <div className="hm-family-reactions">
          {DEFAULT_REACTIONS.map(emoji => {
            const reaction = reacted.get(emoji);
            return (
              <button key={emoji} type="button" className={reaction?.user_reacted ? "is-active" : ""} onClick={() => onReact(item.id, emoji)}>
                {emoji} {reaction?.count || ""}
              </button>
            );
          })}
          <button type="button" onClick={() => onReact(item.id, DEFAULT_REACTIONS[0])}>+</button>
        </div>
      </div>
    </article>
  );
}

function groupSamePerson(items) {
  const result = [];
  const buckets = items.reduce((acc, item) => {
    const key = item.member_email || item.member_name || "member";
    acc[key] = acc[key] || [];
    acc[key].push(item);
    return acc;
  }, {});
  Object.values(buckets).forEach(bucket => {
    if (bucket.length >= 3) {
      result.push({ type: "summary", id: `summary-${bucket[0].member_email || bucket[0].member_name}`, items: bucket });
    } else {
      bucket.forEach(item => result.push({ type: "item", id: item.id, item }));
    }
  });
  return result;
}

function ActivitySection({ title, items, summary, icons, defaultOpen, onReact, onOpenShare }) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const [showAllToday, setShowAllToday] = useState(false);
  const [openSummaries, setOpenSummaries] = useState({});
  const visible = title === "Today" && !showAllToday ? (items || []).slice(0, 5) : (items || []);
  if (!items?.length) return null;
  const itemCount = items.length;
  const fallbackSummary = `${itemCount} thing${itemCount === 1 ? "" : "s"}`;
  const summaryText = summary || fallbackSummary;
  const actionHint = defaultOpen ? "Latest" : open ? "Close" : "Open";
  const previewItems = (items || []).slice(0, 2);
  return (
    <section className={`hm-family-feed-group${open ? " is-open" : ""}`}>
      <button type="button" className="hm-family-feed-group-head" aria-expanded={open} onClick={() => !defaultOpen && setOpen(value => !value)}>
        <span className="hm-family-feed-head-copy">
          <h3>{title}</h3>
          <small>{summaryText}</small>
        </span>
        <span className="hm-family-feed-head-meta">
          {(icons || []).length > 0 && <em>{(icons || []).join(" ")}</em>}
          <b>{actionHint}</b>
          {!defaultOpen && <i>{open ? "⌃" : "⌄"}</i>}
        </span>
      </button>
      {!open && (
        <div className="hm-family-feed-peek">
          {previewItems.map(item => (
            <button key={item.id} type="button" onClick={() => item.share_id ? onOpenShare(item.share_id) : setOpen(true)}>
              <span>{item.content_preview?.icon || "•"}</span>
              <strong>{firstName(item.member_name, item.member_email)} shared {item.content_preview?.name || item.action_text}</strong>
              <em>{timeAgo(item.created_at)}</em>
            </button>
          ))}
          {itemCount > previewItems.length && (
            <button type="button" className="hm-family-feed-peek-more" onClick={() => setOpen(true)}>
              +{itemCount - previewItems.length} more
            </button>
          )}
        </div>
      )}
      {open && (
        <>
          {groupSamePerson(visible).map(entry => {
            if (entry.type === "item") {
              return <FeedItem key={entry.id} item={entry.item} onReact={onReact} onOpenShare={onOpenShare} />;
            }
            const expanded = openSummaries[entry.id];
            const first = entry.items[0];
            return (
              <div key={entry.id} className="hm-family-feed-summary">
                <button type="button" onClick={() => setOpenSummaries(current => ({ ...current, [entry.id]: !expanded }))}>
                  <span className="hm-family-feed-avatar">{initials(first.member_name, first.member_email).slice(0, 1)}</span>
                  <strong>{firstName(first.member_name, first.member_email)} · {entry.items.length} things {title.toLowerCase()}</strong>
                  <em>{expanded ? "⌃" : "›"}</em>
                </button>
                {expanded && entry.items.map(item => (
                  <FeedItem key={item.id} item={item} onReact={onReact} onOpenShare={onOpenShare} />
                ))}
              </div>
            );
          })}
          {title === "Today" && (items || []).length > 5 && !showAllToday && (
            <button type="button" className="hm-family-feed-more" onClick={() => setShowAllToday(true)}>
              Show {(items || []).length - 5} more from today
            </button>
          )}
        </>
      )}
    </section>
  );
}

function ActivityFeed({ feed, onReact, onOpenShare, onOpenArchive }) {
  const today = feed?.today || [];
  const yesterday = feed?.yesterday || {};
  const thisWeek = feed?.this_week || {};
  const hasItems = today.length || yesterday.items?.length || thisWeek.items?.length;
  if (!hasItems) {
    return (
      <div className="hm-family-empty">
        <div>✨</div>
        <strong>No family activity yet</strong>
        <span>Shared items, cooking updates, and reactions will appear here.</span>
      </div>
    );
  }
  return (
    <div className="hm-family-feed">
      <ActivitySection title="Today" items={today} defaultOpen onReact={onReact} onOpenShare={onOpenShare} />
      <ActivitySection title="Yesterday" items={yesterday.items || []} summary={yesterday.summary} icons={yesterday.icons} onReact={onReact} onOpenShare={onOpenShare} />
      <ActivitySection title="This week" items={thisWeek.items || []} summary={thisWeek.summary} icons={thisWeek.icons} onReact={onReact} onOpenShare={onOpenShare} />
      {feed?.has_older && (
        <button type="button" className="hm-family-feed-archive-link" onClick={onOpenArchive}>
          See older activity →
        </button>
      )}
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */
export default function FamilyPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [summary, setSummary]     = useState(null);
  const [lively, setLively]       = useState({ stats: {}, coming_up: [], cooking_tonight: [], today: [], yesterday: { items: [] }, this_week: { items: [] }, has_older: false });
  const [archiveItems, setArchiveItems] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState("activity");
  const [sharePickerOpen, setSharePickerOpen] = useState(false);
  const [inviteSheetOpen, setInviteSheetOpen] = useState(false);
  const [working, setWorking]     = useState("");
  const [error, setError]         = useState("");
  const [actionItem, setActionItem]   = useState(null);   // item for bottom sheet
  const [actionGroupMeta, setActionGroupMeta] = useState(null);
  const showWorking = useMinLoad(Boolean(working), 700);

  // ── Drag-to-dismiss for action sheet (native touch — works on iOS/Android) ──
  const actionSheetRef = useRef(null);

  const closeActionSheet = useCallback(() => {
    setActionItem(null);
    setActionGroupMeta(null);
  }, []);

  useEffect(() => {
    const el = actionSheetRef.current;
    if (!el || !actionItem) return;
    let startY = 0, delta = 0;

    function onStart(e) {
      startY = e.touches[0].clientY;
      delta  = 0;
      el.style.animation  = "none";
      el.style.transition = "none";
    }
    function onMove(e) {
      delta = Math.max(0, e.touches[0].clientY - startY);
      el.style.transform = `translateY(${delta}px)`;
    }
    function onEnd() {
      if (delta > 72) {
        el.style.transition = "transform .22s cubic-bezier(0.4,0,1,1)";
        el.style.transform  = "translateY(110%)";
        setTimeout(closeActionSheet, 230);
      } else {
        el.style.transition = "transform .2s cubic-bezier(0.2,0,0,1)";
        el.style.transform  = "translateY(0)";
        setTimeout(() => { el.style.transition = ""; }, 210);
      }
    }
    el.addEventListener("touchstart", onStart, { passive: true  });
    el.addEventListener("touchmove",  onMove,  { passive: true  });
    el.addEventListener("touchend",   onEnd,   { passive: true  });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove",  onMove);
      el.removeEventListener("touchend",   onEnd);
      el.style.transform = "";
      el.style.transition = "";
      el.style.animation = "";
    };
  }, [actionItem, closeActionSheet]);

  const isFamily = user?.plan === "family"
    || user?.workspace?.plan === "family"
    || user?.workspaces?.some(ws => ws.plan === "family");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [sRes, wRes] = await Promise.allSettled([
        sharingApi.summary(),
        workspacesApi.current(),
      ]);
      if (sRes.status === "fulfilled") setSummary(sRes.value);
      familyApi.activityFeed({ limit: 20 }).then(data => {
        setLively({
          stats: data?.stats || {},
          coming_up: data?.coming_up || [],
          cooking_tonight: data?.cooking_tonight || [],
          today: data?.today || [],
          yesterday: data?.yesterday || { items: [] },
          this_week: data?.this_week || { items: [] },
          has_older: Boolean(data?.has_older),
        });
      }).catch(() => {});
      if (wRes.status === "fulfilled") {
        setWorkspace(wRes.value);
        if (wRes.value?.can_manage_members) {
          const inviteRes = await familyApi.listInvites();
          setPendingInvites(inviteRes?.invites || []);
        } else {
          setPendingInvites([]);
        }
      }
    } catch (err) {
      setError(err.message || "Unable to load Family sharing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const members = useMemo(() => workspace?.members || summary?.members || [], [summary?.members, workspace?.members]);
  const familyWs = workspace?.family_workspace || workspace?.workspace || user?.workspace || {};
  const used    = familyWs.ai_requests_used  ?? user?.ai_requests_used  ?? 0;
  const limit   = familyWs.ai_requests_limit ?? user?.ai_requests_limit ?? 300;
  const percent = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const seatsUsed  = members.length || 1;
  const seatsTotal = familyWs.max_members ?? 3;
  const seatsLeft  = Math.max(seatsTotal - seatsUsed, 0);
  const canManage  = Boolean(workspace?.can_manage_members);

  // Build name→color and name→photo maps for who-chips
  const memberColors = useMemo(() => {
    const map = {};
    const list = members.length
      ? members
      : [{ full_name: user?.full_name, email: user?.email }];
    list.forEach((m, i) => {
      map[firstName(m.full_name, m.email)] = AVATAR_COLORS[i % AVATAR_COLORS.length];
    });
    return map;
  }, [members, user]);

  const memberAvatars = useMemo(() => {
    const map = {};
    const list = members.length
      ? members
      : [{ full_name: user?.full_name, email: user?.email, profile_picture: user?.profile_picture, avatar_url: user?.avatar_url }];
    list.forEach(m => {
      const photo = memberProfilePicture(m);
      if (photo) map[firstName(m.full_name, m.email)] = photo;
    });
    return map;
  }, [members, user]);

  const withGroups = useMemo(() => grouped(summary, "with_me_groups"), [summary]);
  const byGroups   = useMemo(() => grouped(summary, "by_me_groups"),   [summary]);
  const activeGroups = tab === "with" ? withGroups : byGroups;
  const activeCount  = tab === "with"
    ? (summary?.with_me || []).length
    : tab === "by"
      ? (summary?.by_me  || []).length
      : (lively.today || []).length + (lively.yesterday?.items || []).length + (lively.this_week?.items || []).length;


  async function removeItem(item) {
    setWorking(tab === "with" ? "Removing…" : "Stopping sharing…");
    try {
      if (tab === "with") await sharingApi.hide(item.id);
      else await sharingApi.revoke(item.id);
      toast(tab === "with" ? "Removed from Shared with me" : "Sharing stopped", "success");
      await load();
    } catch (err) {
      setError(err.message || "Could not update sharing.");
    } finally { setWorking(""); }
  }

  async function changePermission(item, permission) {
    if ((item.permission || "view") === permission) {
      toast(permission === "edit" ? "Already set to can edit" : "Already set to view only", "info");
      return;
    }
    setWorking(permission === "edit" ? "Allowing edits…" : "Switching to view only…");
    try {
      const result = await sharingApi.updatePermission(item.id, permission);
      const updated = result?.share || { ...item, permission, can_edit: permission === "edit" };
      setSummary(current => {
        if (!current) return current;
        const replace = share => (share.id === item.id ? { ...share, ...updated } : share);
        const replaceGroups = groups => Object.fromEntries(
          Object.entries(groups || {}).map(([label, items]) => [label, (items || []).map(replace)])
        );
        return {
          ...current,
          by_me: (current.by_me || []).map(replace),
          by_me_groups: replaceGroups(current.by_me_groups),
        };
      });
      setActionItem(prev => prev && prev.id === item.id ? { ...prev, ...updated } : prev);
      toast(permission === "edit" ? "Member can edit this item" : "Member is view only", "success");
    } catch (err) {
      setError(err.message || "Could not change permission.");
      toast(err.message || "Could not change permission.", "error");
    } finally {
      setWorking("");
    }
  }

  async function revokeInvite(invite) {
    if (working) return;
    setWorking("Revoking invite…");
    setError("");
    try {
      await familyApi.revokeInvite(invite.id);
      setPendingInvites(current => current.filter(item => item.id !== invite.id));
      toast(`Invite to ${invite.email} revoked`, "success");
    } catch (err) {
      setError(err.message || "Could not revoke that invite.");
      toast(err.message || "Could not revoke that invite.", "error");
    } finally {
      setWorking("");
    }
  }

  async function resendInvite(invite) {
    if (working) return;
    setWorking("Resending invite…");
    setError("");
    try {
      const res = await familyApi.resendInvite(invite.id);
      setPendingInvites(current => current.map(item => item.id === invite.id ? (res.invite || item) : item));
      toast(res.message || `Invite resent to ${invite.email}`, "success");
    } catch (err) {
      setError(err.message || "Could not resend invite.");
      toast(err.message || "Could not resend invite.", "error");
    } finally {
      setWorking("");
    }
  }

  function updateFeedItemCollections(current, updater) {
    const mapItems = items => (items || []).map(item => updater(item));
    return {
      ...current,
      today: mapItems(current.today),
      yesterday: { ...(current.yesterday || {}), items: mapItems(current.yesterday?.items) },
      this_week: { ...(current.this_week || {}), items: mapItems(current.this_week?.items) },
    };
  }

  async function reactToFeedItem(itemId, emoji) {
    let prevLively;
    setLively(current => {
      prevLively = current;
      return updateFeedItemCollections(current, item => {
        if (item.id !== itemId) return item;
        const reactions = [...(item.reactions || [])];
        const existing = reactions.find(reaction => reaction.emoji === emoji);
        if (existing) {
          const nextActive = !existing.user_reacted;
          existing.user_reacted = nextActive;
          existing.count = Math.max(0, (existing.count || 0) + (nextActive ? 1 : -1));
        } else {
          reactions.push({ emoji, count: 1, user_reacted: true });
        }
        return { ...item, reactions };
      });
    });
    try {
      const result = await familyApi.reactToFeedItem(itemId, emoji);
      setLively(current => updateFeedItemCollections(current, item => item.id === itemId ? { ...item, reactions: result.reactions || [] } : item));
    } catch (err) {
      // Roll back to previous state instead of reloading the whole feed
      setLively(prevLively);
      toast(err.message || "Could not update reaction", "error");
    }
  }

  async function openArchive() {
    setWorking("Loading older activity…");
    try {
      const res = await familyApi.activityArchive({ limit: 20 });
      setArchiveItems(res?.items || []);
      toast("Older activity loaded", "success");
    } catch (err) {
      toast(err.message || "Could not load older activity", "error");
    } finally {
      setWorking("");
    }
  }

  if (!isFamily) {
    return (
      <div className="hm-family-page hm-desktop-page-shell">
        <div className="hm-family-upgrade">
          <span>Family</span>
          <h1>Family plan ended</h1>
          <p>Your family plan is no longer active. Your own HouseMind data is safe.</p>
          <button type="button" onClick={() => navigate("/app/settings")}>Start your own Family plan</button>
        </div>
      </div>
    );
  }

  /* ── Render ── */
  const allMembers = members.length
    ? members
    : [{ user_id: user?.id, full_name: user?.full_name, email: user?.email, role: "owner", profile_picture: user?.profile_picture, avatar_url: user?.avatar_url }];

  // Per-member AI usage (approximate split if not available)
  const memberUsage = allMembers.map(m => ({
    name: firstName(m.full_name, m.email),
    used: m.ai_requests_used ?? null,
  }));

  return (
    <div className="hm-family-page hm-desktop-page-shell">
      {showWorking && <SavingNotice title="Working" message={working} />}

      {/* Hero */}
      <section className="hm-family-hero">
        <div>
          <div className="hm-family-hero-label">
            Family <span className="hm-fh-plan-chip">Family plan</span>
          </div>
          <h1>Family sharing</h1>
          <p>Private by default. Share only what you choose.</p>
        </div>
      </section>

      {error && <div className="hm-family-error">{error}</div>}

      {/* Members strip */}
      <div className="hm-fam-members-strip">
        {allMembers.map((m, i) => (
          <div key={m.membership_id || m.user_id || m.email} className="hm-fam-member-chip">
            {memberProfilePicture(m)
              ? <img className="hm-fam-chip-avatar hm-fam-chip-avatar--photo" src={memberProfilePicture(m)} alt={firstName(m.full_name, m.email)} />
              : <div className="hm-fam-chip-avatar" style={{ background: m.avatar_color || AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{initials(m.full_name, m.email)}</div>
            }
            <div className="hm-fam-chip-info">
              <span className="hm-fam-chip-name">
                {firstName(m.full_name, m.email)}
                {m.email === user?.email && <span className="hm-fam-chip-you">You</span>}
              </span>
              <span className="hm-fam-chip-role">{m.role === "owner" ? "Owner" : "Member"}</span>
            </div>
          </div>
        ))}
        {seatsLeft > 0 && canManage && (
          <div className="hm-fam-member-chip hm-fam-member-chip--invite"
            onClick={() => setInviteSheetOpen(true)}>
            <div className="hm-fam-chip-avatar hm-fam-chip-avatar--plus">+</div>
            <div className="hm-fam-chip-info">
              <span className="hm-fam-chip-name" style={{ color: "var(--hm-mint)" }}>Invite</span>
              <span className="hm-fam-chip-role">{seatsLeft} seat{seatsLeft !== 1 ? "s" : ""} left</span>
            </div>
          </div>
        )}
      </div>

      {canManage && pendingInvites.length > 0 && (
        <div className="hm-fam-pending-invites">
          <div className="hm-fam-pending-head">
            <strong>📨 Pending invites</strong>
            <span>Clean up mistyped emails before they are accepted.</span>
          </div>
          <div className="hm-fam-pending-list">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="hm-fam-pending-row">
                <div>
                  <strong>{invite.email}</strong>
                  <span>Expires {fmtDate(invite.expires_at)}</span>
                </div>
                <div className="hm-fam-pending-actions">
                  <button type="button" className="hm-fam-resend-btn" onClick={() => resendInvite(invite)} disabled={Boolean(working)}>
                    Resend
                  </button>
                  <button type="button" className="hm-fam-revoke-btn" onClick={() => revokeInvite(invite)} disabled={Boolean(working)}>
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* AI quota — compact single line */}
      <div className="hm-fam-quota">
        <div className="hm-fam-quota-bar-wrap">
          <div className="hm-fam-quota-bar">
            <div className="hm-fam-quota-fill" style={{ width: `${percent}%` }} />
          </div>
        </div>
        <div className="hm-fam-quota-text">
          <span className="hm-fam-quota-num">{used}<em>/{limit}</em></span>
          <span className="hm-fam-quota-detail">
            {memberUsage.filter(m => m.used !== null).map(m => `${m.name} ${m.used}`).join(" · ")}
            {memberUsage.some(m => m.used !== null) ? " · " : ""}
            Resets {fmtDate(familyWs.reset_date || user?.reset_date)}
          </span>
        </div>
      </div>

      {/* Privacy notice */}
      <div className="hm-family-privacy">
        <strong>Private by default.</strong> Only items you choose to share appear here.
      </div>

      <HouseholdStats stats={lively.stats} />
      <ComingUpBar items={lively.coming_up} onOpen={id => navigate(`/shared/${id}`)} />
      <CookingTonightBar
        items={lively.cooking_tonight}
        currentUserId={user?.id}
        onClear={async () => {
          try {
            await familyApi.clearCookingTonight();
            setLively(prev => ({ ...prev, cooking_tonight: prev.cooking_tonight.filter(s => s.user_id !== user?.id) }));
          } catch (e) { toast(e.message || "Could not clear status", "error"); }
        }}
      />

      <div className="hm-family-share-actions">
        <button type="button" className="hm-family-share-cta" onClick={() => setSharePickerOpen(open => !open)}>
          <span aria-hidden="true">+</span>
          <strong>Share something</strong>
        </button>
      </div>

      {sharePickerOpen && (
        <div className="hm-sheet-overlay hm-family-share-overlay" onClick={() => setSharePickerOpen(false)}>
      <BackButtonTrap onClose={() => setSharePickerOpen(false)} />
          <div className="hm-family-share-sheet hm-family-standard-sheet" onClick={e => e.stopPropagation()}>
            <div className="hm-sheet-handle" />
            <div className="hm-inv-hero hm-family-sheet-hero">
              <button
                type="button"
                className="hm-family-sheet-close"
                aria-label="Close share options"
                onClick={() => setSharePickerOpen(false)}
              >
                ×
              </button>
              <div className="hm-inv-hero-label">Family sharing</div>
              <div className="hm-inv-hero-title">Share <em>something</em></div>
              <div className="hm-inv-hero-sub">Choose where the item lives. Only the specific item you pick will be shared.</div>
            </div>
            <div className="hm-family-share-picker">
              {GROUPS.map(label => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setSharePickerOpen(false);
                    navigate(MODULE_META[label].url);
                  }}
                >
                  <span style={{ background: MODULE_META[label].color }}>{MODULE_META[label].icon}</span>
                  <strong>{label}</strong>
                  <em>›</em>
                </button>
              ))}
            </div>
            <button type="button" className="hm-family-share-cancel" onClick={() => setSharePickerOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="hm-family-tabs">
        <button type="button" className={tab === "activity" ? "is-active" : ""} onClick={() => setTab("activity")}>
          Activity
        </button>
        <button type="button" className={tab === "with" ? "is-active" : ""} onClick={() => setTab("with")}>
          With me <span>{(summary?.with_me || []).length}</span>
        </button>
        <button type="button" className={tab === "by" ? "is-active" : ""} onClick={() => setTab("by")}>
          By me <span>{(summary?.by_me || []).length}</span>
        </button>
      </div>

      {/* Share list */}
      {loading ? (
        <div className="hm-family-loading"><span className="spinner" /> Loading…</div>
      ) : tab === "activity" ? (
        <>
          <ActivityFeed
            feed={lively}
            onReact={reactToFeedItem}
            onOpenShare={id => navigate(`/shared/${id}`)}
            onOpenArchive={openArchive}
          />
          {archiveItems.length > 0 && (
            <section className="hm-family-feed-archive">
              <div className="hm-family-group-head">
                <span>🕘</span>
                <strong>Older activity</strong>
                <em>{archiveItems.length} loaded</em>
              </div>
              {archiveItems.map(item => (
                <FeedItem key={item.id} item={item} onReact={reactToFeedItem} onOpenShare={id => navigate(`/shared/${id}`)} />
              ))}
            </section>
          )}
        </>
      ) : activeCount === 0 ? (
        <EmptyState mode={tab} />
      ) : (
        activeGroups.map(group => (
          <section key={group.label} className="hm-family-group">
            <div className="hm-family-group-head">
              <span style={{ background: group.meta.color }}>{group.meta.icon}</span>
              <strong>{group.label}</strong>
              <em>{group.items.length} item{group.items.length !== 1 ? "s" : ""}</em>
            </div>
            <div className="hm-family-share-list">
              {group.items.map(item => (
                <ShareRow
                  key={item.id}
                  item={item}
                  direction={tab}
                  memberColors={memberColors}
                  memberAvatars={memberAvatars}
                  onAction={() => { setActionItem(item); setActionGroupMeta(group.meta); }}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {/* Invite sheet */}
      <InviteSheet
        isOpen={inviteSheetOpen}
        onClose={() => setInviteSheetOpen(false)}
        onSuccess={msg => { toast(msg, "success"); load(); refreshUser?.(); }}
        onWorkingChange={setWorking}
        currentUser={user}
      />

      {/* Action bottom sheet */}
      {actionItem && (
        <ActionSheet
          item={actionItem}
          direction={tab}
          memberColors={memberColors}
          memberAvatars={memberAvatars}
          groupMeta={actionGroupMeta}
          onClose={closeActionSheet}
          onRemove={() => removeItem(actionItem)}
          onOpenModule={() => navigate(`/shared/${actionItem.id}`)}
          onChangePermission={(permission) => changePermission(actionItem, permission)}
          sheetRef={actionSheetRef}
        />
      )}
    </div>
  );
}
